import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Wire schemas for `/selector/payouts/*` — Wave 2.61 BE-2
 * (TASK-107e-followup-be-payouts).
 *
 * Backed by `customers.selector_payouts` (migration 0056). Audience: the
 * selector cabinet «Мои выплаты» surface (PRD §7.3, design §6.20). RBAC at
 * the controller: `@AdminSelectorOrAbove()` — selector sees own rows only,
 * admin / super_admin see all (defence-in-depth: RLS predicate in 0056
 * already enforces the row scope).
 *
 * Status enum mirrors the DB CHECK constraint:
 *   `pending` → `processing` → `paid` | `rejected`.
 */
export const selectorPayoutStatusSchema = z.enum([
    'pending',
    'processing',
    'paid',
    'rejected',
]);
const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
/**
 * Single-row item in the list. `amount_kop` is bigint kopecks per ADR-010 —
 * fits safely in IEEE-754 (Number.MAX_SAFE_INTEGER ≈ 9e15 kop ≈ 90 trln ₽).
 */
export const selectorPayoutListItemSchema = z.object({
    id: uuidSchema,
    selector_user_id: uuidSchema,
    period_start: isoDateSchema,
    period_end: isoDateSchema,
    amount_kop: z.number().int().nonnegative(),
    status: selectorPayoutStatusSchema,
    method: z.string().nullable(),
    paid_at: isoDateTimeSchema.nullable(),
    notes: z.string().nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
/**
 * Summary tiles shown atop the «Мои выплаты» page (design §6.20).
 *   * `total_pending_kop`    — SUM(amount_kop) WHERE status='pending'.
 *   * `total_processing_kop` — SUM(amount_kop) WHERE status='processing'.
 *   * `total_paid_kop`       — SUM(amount_kop) WHERE status='paid'.
 *
 * Scope matches the list filter scope (selector sees own; admin sees all).
 */
export const selectorPayoutSummarySchema = z.object({
    total_pending_kop: z.number().int().nonnegative(),
    total_processing_kop: z.number().int().nonnegative(),
    total_paid_kop: z.number().int().nonnegative(),
});
export const selectorPayoutListResponseSchema = z.object({
    items: z.array(selectorPayoutListItemSchema),
    summary: selectorPayoutSummarySchema,
    next_cursor: z.string().nullable(),
});
/**
 * List filter (keyset pagination + status + period range). Mirrors the shape
 * of `selectorFilterSchema` so cursor encoding is symmetric.
 *
 * `period_from` / `period_to` — closed-closed range on `period_start`.
 * Server uses `period_start >= period_from` and `period_start <= period_to`.
 */
export const selectorPayoutFilterSchema = z
    .object({
    status: selectorPayoutStatusSchema.optional(),
    period_from: isoDateSchema.optional(),
    period_to: isoDateSchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
})
    .strict();
/**
 * Manual rollup trigger — Wave 2.62 BE-1 (TASK-304-followup).
 *
 * `POST /admin/selector-payouts/rollup` body. Super-admin only. Runs the same
 * aggregation as the monthly cron over an arbitrary `[period_start, period_end)`
 * window (closed-open per the table contract). Used for back-fill and one-off
 * catch-up when the cron missed a tick.
 *
 * Guard rails:
 *   - `period_end > period_start` (strict — empty windows are caller bugs).
 *   - `period_end <= today` — operators cannot roll up the future.
 *   - Window width ≤ 1 year — a guardrail against accidentally rolling up the
 *     entire history in one call (`ON CONFLICT DO NOTHING` would still keep
 *     things idempotent, but the audit fan-out would be confusing).
 */
const MAX_ROLLUP_WINDOW_DAYS = 366;
export const selectorPayoutRollupRequestSchema = z
    .object({
    period_start: isoDateSchema,
    period_end: isoDateSchema,
})
    .strict()
    .superRefine((value, ctx) => {
    const start = Date.parse(value.period_start + 'T00:00:00Z');
    const end = Date.parse(value.period_end + 'T00:00:00Z');
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'period dates must be valid YYYY-MM-DD',
            path: ['period_start'],
        });
        return;
    }
    if (end <= start) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'period_end must be strictly after period_start',
            path: ['period_end'],
        });
    }
    const today = new Date();
    const todayUtcMidnight = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    if (end > todayUtcMidnight + 24 * 60 * 60 * 1000) {
        // Allow period_end up to "tomorrow's midnight" so that running the
        // rollup at 02:00 UTC on the 1st with period_end='YYYY-MM-01' (today)
        // is valid — the cron passes today's midnight as the EXCLUSIVE upper
        // bound for the previous calendar month.
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'period_end must not be in the future',
            path: ['period_end'],
        });
    }
    const widthMs = end - start;
    if (widthMs > MAX_ROLLUP_WINDOW_DAYS * 24 * 60 * 60 * 1000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `rollup window must not exceed ${MAX_ROLLUP_WINDOW_DAYS} days`,
            path: ['period_end'],
        });
    }
});
/**
 * Response shape returned by the manual rollup endpoint. Mirrored by the
 * worker cron logger output (no HTTP surface there). `inserted` reflects the
 * number of NEW `customers.selector_payouts` rows — re-running the same
 * period collides on the UNIQUE constraint and returns `inserted = 0`.
 */
export const selectorPayoutRollupResponseSchema = z.object({
    inserted: z.number().int().nonnegative(),
    period_start: isoDateSchema,
    period_end: isoDateSchema,
});
//# sourceMappingURL=selector-payouts.js.map